import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { fetchKpiQbrReport } from "../../redux/slice/kpi-widget-metrics";
import { PRODUCT_SALES_GROUP_MAP } from "../../utils/constantFiles/constant";
import { isAllProductGroupSelection } from "../MyPerformance/myPerformanceHelpers";
import {
  CURRENT_QUARTER_KEY,
  formatQuarterLabel,
  mapQbrReportToTerritoryOverview,
  priorQuarterIdsMostRecentFirst,
} from "./mapQbrReportToTerritoryOverview";

export const QBR_EXPLORE_PROMPTS = {
  attainmentQtd: "How am I tracking against my goals this quarter?",
  breadthDepth:
    "Give me a list of my breadth (1-2 active patients) and depth (3+ active patients) writers",
  keyMetrics:
    "Give me a breakdown of my referrals this quarter by method of delivery and indication",
  topWriters: "Which HCPs have the most referrals this quarter?",
  competitiveDynamicsYutrepia:
    "Give me the top ten HCPs with the most Yutrepia patients?",
  competitiveDynamicsWinrevair:
    "Give me the top ten HCPs with the most Winrevair patients?",
};

function isRocsProductGroup(value) {
  const key = (value ?? "").toString().trim().toUpperCase();
  if (!key) return false;
  if (key.includes("ROCS") || key === "REMODULIN" || key === "ORENITRAM") {
    return true;
  }
  const mapped = PRODUCT_SALES_GROUP_MAP[value];
  if ((mapped?.salesGroup ?? "").toUpperCase() === "ROCS") return true;
  return Object.values(PRODUCT_SALES_GROUP_MAP).some(
    (entry) =>
      (entry.product ?? "").toUpperCase() === key &&
      (entry.salesGroup ?? "").toUpperCase() === "ROCS",
  );
}

function competitiveDynamicsExplorePrompt(productGroups) {
  const groups = Array.isArray(productGroups)
    ? productGroups
    : [productGroups];
  if (groups.some(isRocsProductGroup)) {
    return QBR_EXPLORE_PROMPTS.competitiveDynamicsWinrevair;
  }
  return QBR_EXPLORE_PROMPTS.competitiveDynamicsYutrepia;
}

/** Fallback for the dropdown until `quarter_info` arrives from the API. */
function currentQuarterLabelFromDate(now = new Date()) {
  const quarter = Math.floor(now.getMonth() / 3) + 1;
  return `Q${quarter} ${now.getFullYear()}`;
}

export function useQBRController({ isActive = true } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const qbrReport = useSelector((state) => state.kpiWidgetMetrics.qbrReport);
  const repProductGroup = useSelector((state) => state.repEmails.productGroup);
  const selectedProduct = useSelector(
    (state) => state.repEmails.selectedProduct,
  );

  // QBR shows one product group at a time. "All" is left out, and a shared
  // "All" selection falls back to the first assigned group. The group is
  // fixed for the sales rep (not user-editable in this view).
  const productOptions = useMemo(
    () => [
      ...new Set(
        (repProductGroup || []).filter(
          (group) => group && !isAllProductGroupSelection(group),
        ),
      ),
    ],
    [repProductGroup],
  );

  const effectiveSelectedProduct = useMemo(() => {
    if (!productOptions.length) return "";
    return productOptions.includes(selectedProduct)
      ? selectedProduct
      : productOptions[0];
  }, [productOptions, selectedProduct]);

  const ranQbrFetch = useRef(false);

  useEffect(() => {
    if (!isActive) return;
    if (ranQbrFetch.current) return;
    ranQbrFetch.current = true;
    dispatch(fetchKpiQbrReport());
  }, [dispatch, isActive]);

  const refresh = useCallback(() => {
    dispatch(fetchKpiQbrReport());
  }, [dispatch]);

  const [selectedQuarterKey, setSelectedQuarterKey] =
    useState(CURRENT_QUARTER_KEY);

  const response = qbrReport?.data;

  const quarterOptions = useMemo(() => {
    const quarterInfo = response?.quarter_info ?? {};
    const currentLabel =
      formatQuarterLabel(quarterInfo.current_quarter) ||
      currentQuarterLabelFromDate();

    const options = [
      {
        key: CURRENT_QUARTER_KEY,
        label: currentLabel,
      },
    ];

    priorQuarterIdsMostRecentFirst(response?.prior_quarters).forEach(
      (quarterId) => {
        const snapshot = response?.prior_quarters?.[quarterId];
        const fromMeta = snapshot?.snapshot_meta?.quarter_id ?? quarterId;
        options.push({
          key: quarterId,
          label: formatQuarterLabel(fromMeta),
        });
      },
    );

    return options;
  }, [response]);

  useEffect(() => {
    const validKeys = new Set(quarterOptions.map((option) => option.key));
    if (!validKeys.has(selectedQuarterKey)) {
      setSelectedQuarterKey(CURRENT_QUARTER_KEY);
    }
  }, [quarterOptions, selectedQuarterKey]);

  const overview = useMemo(
    () =>
      mapQbrReportToTerritoryOverview({
        qbrReportResponse: response,
        selectedProduct: effectiveSelectedProduct,
        quarterKey: selectedQuarterKey,
      }),
    [response, effectiveSelectedProduct, selectedQuarterKey],
  );

  const goToChatWithPrompt = useCallback(
    (prompt) => {
      const params = new URLSearchParams(location.search);
      params.set("tab", "repassist");
      params.set("prefillKey", String(Date.now()));
      navigate(
        { pathname: "/", search: params.toString() },
        {
          replace: false,
          state: {
            prefillPrompt: prompt,
            prefillId: Date.now(),
          },
        },
      );
    },
    [location.search, navigate],
  );

  const onExplore = useMemo(
    () => ({
      attainmentQtd: () => goToChatWithPrompt(QBR_EXPLORE_PROMPTS.attainmentQtd),
      breadthDepth: () => goToChatWithPrompt(QBR_EXPLORE_PROMPTS.breadthDepth),
      keyMetrics: () => goToChatWithPrompt(QBR_EXPLORE_PROMPTS.keyMetrics),
      topWriters: () => goToChatWithPrompt(QBR_EXPLORE_PROMPTS.topWriters),
      competitiveDynamics: () =>
        goToChatWithPrompt(
          competitiveDynamicsExplorePrompt(productOptions),
        ),
    }),
    [productOptions, goToChatWithPrompt],
  );

  return {
    overview,
    quarterOptions,
    selectedQuarterKey,
    onQuarterChange: setSelectedQuarterKey,
    onExplore,
    isLoading: qbrReport?.isLoading ?? false,
    isError: qbrReport?.isError ?? false,
    hasData: Boolean(response),
    refresh,
    productGroupLabel: effectiveSelectedProduct,
  };
}

export default useQBRController;
